import { BenjiSim } from "./benjiSim";
import { VineFieldSim, VineState } from "./vineState";
import { MAX_STEPS, multiplierForStep } from "./crashEngine";
import { Rng } from "./rng";
import {
  CATCH,
  CRASH_OFFSCREEN_PADDING,
  INITIAL_SWING_ANGLE,
  LAUNCH,
  MIN_VISIBLE_WORLD_HEIGHT,
  MIN_VISIBLE_WORLD_WIDTH,
  VINES,
  clamp,
} from "./config";
import type { RopeClosestPoint } from "./ropeGeometry";

export type GameState = "idle" | "waiting" | "jumping" | "cashed" | "crashed";
export type RoundOutcome = "in-progress" | "cashed" | "busted";

export interface RoundConfig {
  /** Bet for this round (payout = bet * multiplier). Wallet stays external. */
  bet: number;
  /** Provably-fair derived seed for level generation + snap rolls. */
  levelSeed: number;
  /** Separate seed for cosmetic post-bust spin (does not affect outcome). */
  fxSeed: number;
  /** Authoritative viewport — MUST match between client and server replay. */
  viewportW?: number;
  viewportH?: number;
}

export type RoundEventKind = "sparks" | "win" | "miss";
export interface RoundEvent {
  kind: RoundEventKind;
  x: number;
  y: number;
  count: number;
}

export interface RoundResult {
  outcome: RoundOutcome;
  step: number;
  multiplier: number;
  bet: number;
  payout: number;
}

/**
 * Authoritative, deterministic round. Same seed + same input sequence (jump /
 * cashOut at the same step) yields the same outcome on client and server.
 *
 * Holds NO wallet/balance — it only models step / multiplier / outcome and the
 * physical world (Benji + vines). The orchestrator (client) or the server
 * applies money based on {@link RoundResult}.
 *
 * State machine and physics ported behaviour-preserving from the client
 * `Game`; `Math.random` replaced by the injected level RNG; per-frame `dt`
 * replaced by the fixed `SIM_DT` driven through {@link step}.
 */
export class Round {
  readonly benji: BenjiSim;
  private readonly field = new VineFieldSim();
  private readonly levelRng: Rng;

  private _state: GameState = "idle";
  private _step = 0;
  private _multiplier = 1.0;
  private bet: number;
  private stakedBet: number;
  private betLocked = false;
  private _outcome: RoundOutcome = "in-progress";

  private viewportW: number;
  private viewportH: number;

  private ignoredJumpVine: VineState | null = null;
  private slidingCatchVine: VineState | null = null;

  private events: RoundEvent[] = [];
  private ticks = 0;

  constructor(cfg: RoundConfig) {
    this.levelRng = new Rng(cfg.levelSeed);
    this.benji = new BenjiSim(new Rng(cfg.fxSeed));
    this.bet = cfg.bet;
    this.stakedBet = cfg.bet;
    this.viewportW = cfg.viewportW ?? MIN_VISIBLE_WORLD_WIDTH;
    this.viewportH = cfg.viewportH ?? MIN_VISIBLE_WORLD_HEIGHT;
    this.spawnIdleScene();
    this.settle(50);
  }

  // ---- public read API -----------------------------------------------------
  get state(): GameState { return this._state; }
  get step(): number { return this._step; }
  get multiplier(): number { return this._multiplier; }
  get outcome(): RoundOutcome { return this._outcome; }
  vines(): readonly VineState[] { return this.field.vines; }
  /** Count of `tick()` calls since construction — addresses inputs by tick. */
  getTick(): number { return this.ticks; }

  setViewport(w: number, h: number) {
    this.viewportW = w;
    this.viewportH = h;
  }

  /** Whether the bet is still editable (not yet staked into a live round). */
  get isBetLocked(): boolean {
    return this.betLocked;
  }

  /** Change the bet before it is staked (no-op once locked at first jump). */
  setBet(bet: number) {
    if (this.betLocked) return;
    this.bet = bet;
    this.stakedBet = bet;
  }

  drainEvents(): RoundEvent[] {
    const e = this.events;
    this.events = [];
    return e;
  }

  result(): RoundResult {
    const payout =
      this._outcome === "cashed"
        ? Math.floor(this.stakedBet * this._multiplier * 100) / 100
        : 0;
    return {
      outcome: this._outcome,
      step: this._step,
      multiplier: this._multiplier,
      bet: this.stakedBet,
      payout,
    };
  }

  // ---- inputs --------------------------------------------------------------
  startRound() {
    if (this._state !== "idle") return;

    const existingSwingVine = this.benji.getSwingVine();
    this._step = 0;
    this._multiplier = 1.0;

    let spawnedScene = false;
    if (this.field.vines.length === 0) {
      this.field.spawn(0, VINES.pivotY, this.randomLength(), this.windSeed());
      spawnedScene = true;
    }
    this.ensureVineBuffer();

    if (!existingSwingVine) {
      this.benji.startHang(this.field.vines[0], INITIAL_SWING_ANGLE, 0);
    }
    if (spawnedScene) this.settle(30);

    this.setState("waiting");
  }

  jump() {
    if (this._state !== "waiting") return;
    if (this._step >= MAX_STEPS) return;

    const curVine = this.benji.getSwingVine() as VineState | null;
    if (!curVine) return;

    this.ensureVineBuffer();
    const curIdx = this.field.vines.indexOf(curVine);
    const nextVine = this.field.vines[curIdx + 1];
    if (!nextVine) return;

    if (this._step === 0) {
      this.betLocked = true;
      this.stakedBet = this.bet;
    }

    curVine.active = false;
    curVine.releaseTip();

    this.ignoredJumpVine = curVine;
    this.slidingCatchVine = null;

    const releaseVel = this.benji.getVelocity();
    const swingSpeed = Math.hypot(releaseVel.vx, releaseVel.vy);
    const strength = clamp(swingSpeed / LAUNCH.referenceSpeed, 0, 1);
    const kick = LAUNCH.kickBase + LAUNCH.kickStrength * strength;

    let vx: number;
    let vy: number;
    if (swingSpeed > 1) {
      const launchSpeed = Math.min(swingSpeed + kick, LAUNCH.maxSpeed);
      vx = (releaseVel.vx / swingSpeed) * launchSpeed;
      vy = (releaseVel.vy / swingSpeed) * launchSpeed;
    } else {
      vx = kick * 0.5;
      vy = -kick * 0.2;
    }
    this.benji.startFly(vx, vy);
    this.setState("jumping");
    this.ensureVineBuffer();
  }

  cashOut(): boolean {
    if (this._state !== "waiting" || this._step < 1) return false;
    this.setState("cashed");
    this._outcome = "cashed";
    this.benji.startCelebrate();
    this.emit("win", this.benji.worldX, this.benji.worldY, 28);
    return true;
  }

  /** Reset back to a fresh idle scene (orchestrator decides the next round). */
  resetToIdle() {
    this._multiplier = 1.0;
    this._step = 0;
    this._outcome = "in-progress";
    this.betLocked = false;
    this.ignoredJumpVine = null;
    this.slidingCatchVine = null;
    this.field.clear();
    this.benji.resetState();
    this.spawnIdleScene();
    this.settle(40);
    this.setState("idle");
  }

  // ---- fixed-step simulation ----------------------------------------------
  /**
   * Advance exactly one `SIM_DT`. Mirrors the old Game.update ordering.
   * Named `tick()` (not `step()`) so it does not shadow the `step` rung
   * counter getter.
   */
  tick() {
    const slidingNow =
      this._state === "jumping" &&
      this.slidingCatchVine !== null &&
      this.benji.getMode() === "slide";

    if (slidingNow) this.field.updateAll(this.cullLeftWorldX());

    this.benji.step();

    if (this._state === "jumping" && !this.slidingCatchVine) {
      this.ensureVinesAhead();
      this.checkCatch();
      if (
        this._state === "jumping" &&
        this.benji.worldY > this.viewportH + CRASH_OFFSCREEN_PADDING
      ) {
        this.crashBenji(this.benji.worldX, this.benji.worldY);
      }
    }

    if (
      this._state === "jumping" &&
      this.slidingCatchVine &&
      this.benji.getMode() === "hang"
    ) {
      const vine = this.slidingCatchVine;
      this.slidingCatchVine = null;
      this.completeCatchOnVine(vine);
    }

    if (!slidingNow) this.field.updateAll(this.cullLeftWorldX());

    this.ticks += 1;
  }

  // ---- catch / bust --------------------------------------------------------
  private checkCatch() {
    const benji = this.benji;
    let best: { vine: VineState; point: RopeClosestPoint } | null = null;

    for (const vine of this.field.vines) {
      if (vine === this.ignoredJumpVine) continue;
      if (
        vine.active ||
        vine.broken ||
        Math.max(vine.pivotWorldX, vine.tipX()) < benji.worldX - CATCH.missGraceX
      )
        continue;
      const point = vine.sweptClosestPointTo(
        benji.prevWorldX,
        benji.prevWorldY,
        benji.worldX,
        benji.worldY,
      );
      if (point.distance > CATCH.radius) continue;
      if (!best || point.distance < best.point.distance) best = { vine, point };
    }

    if (!best) return;
    if (this.levelRng.next() < CATCH.snapChance) {
      this.breakVineOnCatch(best.vine, best.point);
    } else {
      this.catchOnVine(best.vine, best.point);
    }
  }

  private catchOnVine(vine: VineState, catchPoint: RopeClosestPoint) {
    const theta = Math.atan2(
      catchPoint.x - vine.pivotWorldX,
      catchPoint.y - vine.pivotWorldY,
    );
    const catchLength = Math.hypot(
      catchPoint.x - vine.pivotWorldX,
      catchPoint.y - vine.pivotWorldY,
    );
    const vel = this.benji.getVelocity();
    const tangentialSpeed = vel.vx * Math.cos(theta) - vel.vy * Math.sin(theta);
    const omega = (tangentialSpeed / catchLength) * CATCH.energyLoss;
    vine.applyImpulseAt(
      catchPoint.distanceAlong,
      vel.vx * CATCH.ropeImpulse,
      vel.vy * CATCH.ropeImpulse,
    );

    this.benji.worldX = catchPoint.x;
    this.benji.worldY = catchPoint.y;
    this.benji.startSlide(vine, catchPoint.distanceAlong, omega);
    this.ignoredJumpVine = null;
    this.slidingCatchVine = vine;

    this.emit("sparks", catchPoint.x, catchPoint.y, 8);
  }

  private completeCatchOnVine(_vine: VineState) {
    this._step += 1;
    this._multiplier = multiplierForStep(this._step);

    this.emit("sparks", this.benji.worldX, this.benji.worldY, 5);

    if (this._step >= MAX_STEPS) {
      this.setState("waiting");
      this.cashOut();
      return;
    }
    this.setState("waiting");
    this.ensureVineBuffer();
  }

  private breakVineOnCatch(vine: VineState, catchPoint: RopeClosestPoint) {
    this.benji.worldX = catchPoint.x;
    this.benji.worldY = catchPoint.y;
    this.ignoredJumpVine = null;
    const vel = this.benji.getVelocity();
    vine.breakAt(catchPoint.distanceAlong, vel.vx * 0.02, vel.vy * 0.02 - 10);
    this.crashBenji(catchPoint.x, catchPoint.y);
  }

  private crashBenji(effectWorldX: number, effectWorldY: number) {
    this.emit("miss", effectWorldX, effectWorldY, 22);
    this.ignoredJumpVine = null;
    const vel = this.benji.getVelocity();
    this.benji.startFall(vel.vx, vel.vy, this.benji.bodyRotation);
    this.setState("crashed");
    this._outcome = "busted";
  }

  // ---- level generation ----------------------------------------------------
  private randomLength(): number {
    return (
      VINES.minHeight + this.levelRng.next() * (VINES.maxHeight - VINES.minHeight)
    );
  }

  private randomVineSpacing(): number {
    return (
      VINES.spacing.min +
      this.levelRng.next() * (VINES.spacing.max - VINES.spacing.min)
    );
  }

  private windSeed(): number {
    return this.levelRng.next() * Math.PI * 2;
  }

  private ensureVineBuffer(buffer = VINES.bufferSize) {
    const vines = this.field.vines;
    const curVine = this.benji.getSwingVine() as VineState | null;
    let curIdx = curVine ? vines.indexOf(curVine) : -1;
    if (curIdx < 0) curIdx = 0;
    const targetCount = curIdx + 1 + buffer;
    while (vines.length < targetCount) {
      const lastX = vines.length ? vines[vines.length - 1].pivotWorldX : 0;
      const nextX = lastX + this.randomVineSpacing();
      this.field.spawn(nextX, VINES.pivotY, this.randomLength(), this.windSeed());
    }
  }

  private ensureVinesAhead() {
    const vines = this.field.vines;
    const targetX = this.benji.worldX + this.viewportW * 2;
    while (!vines.length || vines[vines.length - 1].pivotWorldX < targetX) {
      const lastX = vines.length ? vines[vines.length - 1].pivotWorldX : 0;
      this.field.spawn(
        lastX + this.randomVineSpacing(),
        VINES.pivotY,
        this.randomLength(),
        this.windSeed(),
      );
    }
  }

  private spawnIdleScene() {
    this.field.spawn(0, VINES.pivotY, this.randomLength(), this.windSeed());
    this.benji.startHang(this.field.vines[0], INITIAL_SWING_ANGLE * 0.5, 0);
    this.ensureVineBuffer();
  }

  /** Deterministic, camera-independent left cull edge (client==server). */
  private cullLeftWorldX(): number {
    return this.benji.worldX - this.viewportW;
  }

  /** Settle the ropes for `n` fixed steps (Benji held still). */
  private settle(n: number) {
    for (let i = 0; i < n; i++) {
      for (const v of this.field.vines) v.step();
    }
  }

  private setState(state: GameState) {
    this._state = state;
    this.benji.setSwingPump(state === "waiting");
  }

  private emit(kind: RoundEventKind, x: number, y: number, count: number) {
    this.events.push({ kind, x, y, count });
  }

  /** Stable digest of the whole authoritative state — golden tests / divergence. */
  stateHash(): string {
    let h = 0x811c9dc5;
    const mix = (n: number) => {
      h ^= n | 0;
      h = Math.imul(h, 0x01000193);
    };
    const q = (v: number) => Math.round(v * 1000) | 0;
    mix(this._step);
    mix(this._state.length);
    mix(q(this._multiplier));
    mix(q(this.benji.worldX));
    mix(q(this.benji.worldY));
    mix(q(this.benji.bodyRotation));
    mix(this.field.vines.length);
    for (const v of this.field.vines) {
      mix(q(v.pivotWorldX));
      for (const c of v.rope.stateHash()) mix(c.charCodeAt(0));
    }
    return (h >>> 0).toString(16).padStart(8, "0");
  }
}
